import React from "react";
import { ExternalLink, Sparkles } from "lucide-react";
import { projects, resumeUrl, projectPortfolioUrl } from "../mock";

export default function Projects() {
  const filtered = projects;
  const [openFinancials, setOpenFinancials] = React.useState({});
  const [scrollTargetId, setScrollTargetId] = React.useState(null);

  React.useEffect(() => {
    if (!scrollTargetId) return;

    const el = document.getElementById(scrollTargetId);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setScrollTargetId(null);
  }, [scrollTargetId]);

  const slugify = (s) =>
    String(s)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  return (
    <div className="page" style={{ paddingTop: 96 }}>
      <div
        style={{
          padding: "0 clamp(2.2rem, 6vw, 8rem)",
          maxWidth: 1100,
          margin: "0 auto",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 14,
            flexWrap: "wrap",
            alignItems: "flex-end",
          }}
        >
          <div>
            <div className="contact-label-luxe" style={{ marginBottom: 12 }}>
              <Sparkles size={14} />
              <span>PROJECTS</span>
            </div>
            <h1
              className="contact-title-luxe"
              style={{ margin: 0, fontSize: "clamp(2.2rem, 4.2vw, 3.2rem)" }}
            >
              Analytical work,
              <br />
              <span className="contact-title-gradient">built for decisions</span>
            </h1>
          </div>

          <a
            href={resumeUrl}
            className="luxe-nav__link luxe-nav__link--active"
            target="_blank"
            rel="noreferrer"
          >
            Download CV
          </a>
        </div>

        {/* Filter UI removed; showing all projects by default */}

        <div
          style={{
            marginTop: 26,
            display: "grid",
            gridTemplateColumns: "repeat(12, 1fr)",
            gap: 18,
          }}
        >
          {filtered.map((p) => {
            const financialsId = p.financials ? `project-financials-${slugify(p.title)}` : null;
            const isFinancialsOpen = !!openFinancials[p.title];
            const toggleFinancials = () => {
              if (!financialsId) return;
              setOpenFinancials((prev) => {
                const nextOpen = !prev[p.title];
                if (nextOpen) setScrollTargetId(financialsId);
                return { ...prev, [p.title]: nextOpen };
              });
            };

            return (
              <article
                key={p.title}
                className="pillars-scrollpin__content"
                style={{
                  gridColumn: "span 12",
                  padding: 24,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 14,
                    flexWrap: "wrap",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 11,
                        letterSpacing: ".28em",
                        textTransform: "uppercase",
                        color: "rgba(255,182,217,0.8)",
                      }}
                    >
                      {p.year}
                    </div>
                    <h2
                      style={{
                        margin: "10px 0 0",
                        fontFamily: "Playfair Display, Georgia, serif",
                        fontSize: 26,
                        lineHeight: 1.15,
                      }}
                    >
                      {p.title}
                    </h2>
                    <p
                      style={{
                        marginTop: 10,
                        color: "rgba(255,255,255,0.8)",
                        lineHeight: 1.8,
                      }}
                    >
                      {p.description}
                    </p>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      flexWrap: "wrap",
                      justifyContent: "flex-end",
                    }}
                  >
                    {p.pdfUrl ? (
                      <>
                        <a
                          className="luxe-nav__link"
                          href={p.pdfUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Open business plan PDF"
                        >
                          Business Plan{" "}
                          <ExternalLink
                            size={14}
                            style={{ marginLeft: 6, display: "inline" }}
                          />
                        </a>
                        {p.pitchDeckUrl ? (
                          <a
                            className="luxe-nav__link"
                            href={p.pitchDeckUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Open pitch deck PDF"
                          >
                            Pitch Deck{" "}
                            <ExternalLink
                              size={14}
                              style={{ marginLeft: 6, display: "inline" }}
                            />
                          </a>
                        ) : null}
                      </>
                    ) : (
                      <a
                        className="luxe-nav__link"
                        href={p.viewUrl || projectPortfolioUrl}
                        target="_blank"
                        rel="noreferrer"
                        aria-label="Open project resources in Google Drive"
                      >
                        View{" "}
                        <ExternalLink
                          size={14}
                          style={{ marginLeft: 6, display: "inline" }}
                        />
                      </a>
                    )}

                    {p.financials ? (
                      <button
                        type="button"
                        className="luxe-nav__link"
                        onClick={toggleFinancials}
                        aria-expanded={isFinancialsOpen ? "true" : "false"}
                        style={{
                          cursor: "pointer",
                          border: "none",
                          background: "transparent",
                          font: "inherit",
                        }}
                      >
                        {isFinancialsOpen ? "Hide Financials" : "View Financials"}
                      </button>
                    ) : null}
                  </div>
                </div>

                <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {p.tags.map((t) => (
                    <span key={t} className="pillars-scrollpin__output">
                      {t}
                    </span>
                  ))}
                </div>

                <ul style={{ marginTop: 14, color: "rgba(255,255,255,0.78)", lineHeight: 1.8 }}>
                  {p.bullets.map((b) => (
                    <li key={b} style={{ marginTop: 6 }}>
                      {b}
                    </li>
                  ))}
                </ul>

                {p.financials && isFinancialsOpen ? (
                  <div
                    id={financialsId}
                    style={{
                      marginTop: 16,
                      padding: 16,
                      borderRadius: 18,
                      border: "1px solid rgba(255,182,217,0.14)",
                      background: "rgba(255,255,255,0.02)",
                      backdropFilter: "blur(16px)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        fontSize: 11,
                        letterSpacing: ".28em",
                        textTransform: "uppercase",
                        color: "rgba(255,182,217,0.8)",
                        marginBottom: 10,
                      }}
                    >
                      <Sparkles size={14} />
                      <span>Financial Highlights</span>
                    </div>
                    <ul
                      style={{
                        margin: 0,
                        paddingLeft: 18,
                        color: "rgba(255,255,255,0.78)",
                        lineHeight: 1.8,
                      }}
                    >
                      {p.financials.bullets.map((b) => (
                        <li key={b} style={{ marginTop: 6 }}>
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>

        <div style={{ height: 80 }} />
      </div>
    </div>
  );
}
